/**
 * RFC-0392: WebSocket connection for real-time chat events.
 *
 * Replaces the SSE streaming in useAgentChat. Maintains a persistent
 * WebSocket connection to /ws/chat/{projectId} with automatic reconnection
 * and exponential backoff.
 *
 * Terminal error code 4001 (missing auth) suppresses reconnection.
 * 4003 (expired token) allows reconnection — getAuthToken() fetches a fresh token.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { getConfiguredApiBase, getAuthToken, createDebug } from '@ponpon/api-client'
const log = createDebug('dw:chat:socket')
const wsEventLog = createDebug('dw:chat:ws-event')

export interface ChatEventQuestionOption {
  value?: string
  label?: string
  description?: string
}

export interface ChatEventExecutionPlanNode {
  node_id?: string
  type?: string
  action?: string | null
  label?: string
  sparks?: number
}

export interface ChatEventQuestionMetadata {
  nodes?: ChatEventExecutionPlanNode[]
  total_sparks?: number
  // Variant: ``ad_image_upload`` — MIME types the picker should accept.
  accept?: string
  // Variant: ``ad_ready`` — rendered video URL + display metadata.
  video_url?: string
  duration_s?: number
  cost_sparks?: number
  aspect?: string
  poster_url?: string | null
  // Variant: ``ad_url_confirm`` — scraped product preview.
  source_url?: string
  product_name?: string
  product_descriptor?: string
  hero_image_path?: string
  price_text?: string
  warnings?: string[]
}

export interface ChatEventQuestion {
  question_type?: string
  tool_use_id?: string
  question?: string
  options?: ChatEventQuestionOption[]
  allow_custom_input?: boolean
  header?: string
  answer?: string | null
  metadata?: ChatEventQuestionMetadata
}

export interface ChatEventMessage {
  id?: string
  role?: string
  agent?: string
  content?: string
  referenced_node_ids?: string[]
  timestamp?: string
  questions?: ChatEventQuestion[]
  isStreaming?: boolean
  wasStreamed?: boolean
  isSystemNotification?: boolean
}

export interface ChatEvent extends ChatEventMessage {
  type: string
  status?: string
  messages?: ChatEventMessage[]
  error?: string | null
  message?: string
  message_id?: string
  has_tool_use?: boolean
  node_id?: string
  code?: number
}

function isChatEvent(value: Partial<ChatEvent> | null): value is ChatEvent {
  return value !== null && typeof value.type === 'string'
}

interface UseChatSocketOptions {
  projectId: string
  enabled?: boolean
  onEvent: (event: ChatEvent) => void
}

interface UseChatSocketResult {
  connected: boolean
}

function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

const MIN_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

/** Close codes that indicate a terminal auth error — retrying won't help.
 *  4003 (expired token) is NOT terminal: reconnect fetches a fresh token via getAuthToken(). */
const TERMINAL_CLOSE_CODES = new Set([4001])

/** Liveness watchdog: send a ping every Nms; force reconnect if no inbound
 *  message arrives for Mms. Catches silent disconnects (server killed but
 *  TCP/LB don't surface the close). 2s/5s = ~5s detection. */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000

export function useChatSocket({
  projectId,
  enabled = true,
  onEvent,
}: UseChatSocketOptions): UseChatSocketResult {
  const [connected, setConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(MIN_RECONNECT_DELAY)
  const onEventRef = useRef(onEvent)
  const mountedRef = useRef(true)
  /** Set to true when a terminal error event is received — suppresses reconnect. */
  const terminalErrorRef = useRef(false)
  /** Tracks how many times this connection has reconnected (for diagnostics). */
  const reconnectCountRef = useRef(0)

  onEventRef.current = onEvent

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setConnected(false)
  }, [])

  useEffect(() => {
    mountedRef.current = true
    terminalErrorRef.current = false
    if (!enabled || !projectId) {
      cleanup()
      return
    }

    let cancelled = false
    // Heartbeat state: lastMessageAt resets on any inbound message;
    // heartbeatTimer drives both ping send + dead-timeout check.
    let lastMessageAt = Date.now()
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null

    const stopHeartbeat = () => {
      if (heartbeatTimer != null) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
    }

    const startHeartbeat = (ws: WebSocket) => {
      stopHeartbeat()
      heartbeatTimer = setInterval(() => {
        if (cancelled) return
        if (Date.now() - lastMessageAt > HEARTBEAT_DEAD_MS) {
          wsEventLog('heartbeat-timeout, forcing reconnect')
          stopHeartbeat()
          // ws.close() triggers onclose → scheduleReconnect()
          try { ws.close() } catch { /* already closed */ }
          return
        }
        if (ws.readyState !== WebSocket.OPEN) return
        try {
          ws.send('ping')
        } catch {
          // send failed — connection probably dead
          stopHeartbeat()
          try { ws.close() } catch { /* already closed */ }
        }
      }, HEARTBEAT_INTERVAL_MS)
    }

    // When tab returns to foreground, reset lastMessageAt so a long
    // background-throttled gap doesn't immediately trigger a false-positive
    // reconnect. The next ping/pong cycle will surface a real disconnect
    // within HEARTBEAT_DEAD_MS.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastMessageAt = Date.now()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    const connect = async () => {
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      const token = await getAuthToken()
      if (cancelled) return

      const params = new URLSearchParams()
      if (token) {
        params.set('token', token)
      }
      const queryString = params.toString()
      const url = `${wsBase}/ws/chat/${projectId}${queryString ? `?${queryString}` : ''}`

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        if (cancelled) return
        log('connected projectId=%s', projectId)
        wsEventLog('connect projectId=%s reconnectCount=%d', projectId, reconnectCountRef.current)
        setConnected(true)
        reconnectDelayRef.current = MIN_RECONNECT_DELAY
        lastMessageAt = Date.now()
        startHeartbeat(ws)
      }

      ws.onmessage = (event) => {
        if (cancelled) return
        // Reset watchdog on any inbound message — including the pong, keepalive,
        // or any real event. This is what makes the silent-disconnect case
        // detectable.
        lastMessageAt = Date.now()
        try {
          const parsed: Partial<ChatEvent> | null = JSON.parse(event.data)
          if (!isChatEvent(parsed)) return
          const data = parsed
          if (data.type === 'keepalive' || data.type === 'pong') return

          wsEventLog('message type=%s', data.type)

          // Handle server-sent error events (accept-first error handling).
          // Terminal auth errors (4001) suppress reconnection.
          if (data.type === 'error' && typeof data.code === 'number') {
            if (TERMINAL_CLOSE_CODES.has(data.code)) {
              terminalErrorRef.current = true
              wsEventLog('terminal-error code=%d', data.code)
            }
          }

          onEventRef.current(data)
        } catch {
          // ignore non-JSON messages (e.g. "pong")
        }
      }

      ws.onclose = (event) => {
        if (cancelled) return
        log('disconnected code=%d reason=%s', event.code, event.reason)
        wsEventLog('disconnect code=%d reason=%s wasTerminal=%s', event.code, event.reason, terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code))
        setConnected(false)
        stopHeartbeat()

        // Don't reconnect on terminal auth errors — they won't resolve.
        if (terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code)) {
          return
        }

        scheduleReconnect()
      }

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      }
    }

    const scheduleReconnect = async () => {
      if (cancelled || !mountedRef.current) return
      const delay = reconnectDelayRef.current
      reconnectCountRef.current++
      log('reconnect in %dms', delay)
      wsEventLog('reconnect-scheduled delayMs=%d attempt=%d', delay, reconnectCountRef.current)
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY)
      reconnectTimerRef.current = setTimeout(() => {
        if (!cancelled && mountedRef.current) {
          connect()
        }
      }, delay)
    }

    connect()

    return () => {
      cancelled = true
      mountedRef.current = false
      stopHeartbeat()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      cleanup()
    }
  }, [projectId, enabled, cleanup])

  return { connected }
}
